import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common';
import { fundSlug, fundTab, isFundTab, useAdminPanelContext, type PanelTab } from '@/contexts/AdminPanelContext';
import { getAccessibleAdminSections, getAccessibleRecognitionSections } from '@/contexts/UserContext';
import { useVisibleNavLinks } from '@/hooks/useVisibleNavLinks';
import type { RecognitionAdminSection } from '@/auth/accessMatrix';
import type { AdminSection } from '@/types/admin.types';
import type { BuilderState } from '@/types/survey.types';
import { LEGACY_SECTION_SLUGS, renderAdminSection } from '@/views/recognition/admin/sections';
import { SectionErrorBoundary } from './components/SectionErrorBoundary';
import { SurveyBuilderView } from './SurveyBuilderView';
import { SurveysTab } from './tabs/SurveysTab';
import { TemplatesTab } from './tabs/TemplatesTab';
// Catálogos maestros: antes subpestañas de Templates, ahora secciones propias del grupo Admin
// General. Se reutilizan tal cual (renderizan de forma independiente).
import { CatalogoSubTab } from './tabs/TemplatesTab/CatalogoSubTab';
import { OUsSubTab } from './tabs/TemplatesTab/OUsSubTab';
// Pulso → Roles y Permisos de Pulso (tabla + modal, calcada de la de Brillamos).
import { RolesPulsoTab } from './tabs/RolesPulsoTab';
import { RulesTab } from './tabs/RulesTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { SystemTab } from './tabs/SystemTab';
import { MonitoreoTab } from './tabs/MonitoreoTab';
import { TrazabilidadTab } from './tabs/TrazabilidadTab';
import { JobsTab } from './tabs/JobsTab';
import { GestionUsuariosTab } from './tabs/GestionUsuariosTab';
// Admin General → Administradores (alta/baja de admins GLOBALES). Antes era la entrada lateral
// «Administradores» (`/usuarios`); 2026-10-29 pasó a ser sección del panel.
import { GestionAdministradores } from '@/views/UsersAdminView/GestionAdministradores';
import { AnalyticsView } from '@/views/AnalyticsView';
import './AdminView.css';

// La visibilidad de cada sección la define accessMatrix (src/auth/accessMatrix.ts) a partir de
// TODOS los roles del usuario más sus permisos granulares. La administración de usuarios ya no
// vive aquí: es una vista propia del menú principal.

// Orden en que se listan las secciones en el menú lateral del panel.
const TAB_ORDER: AdminSection[] = [
  'catalogoFundamentales', 'catalogoOus',
  'surveys', 'templates', 'analytics', 'rules', 'notifications', 'rolesPulso',
  'system', 'monitor', 'trace', 'jobs', 'gestionUsuarios', 'administradores',
];

// ─── Bloque "Fundamentales" (P4-1) ───────────────────────────────────────────
// El panel hospeda DOS bloques: las secciones de Pulso (arriba) y la administración del Programa
// de Reconocimiento (abajo), igual que la maqueta (`index.html:307-312` + `fundamentales-admin.js`).
// Las 12 secciones ya existían en `views/recognition/admin/`; lo que faltaba era que alguien las
// consumiera.
//
// `FundTab`/`PanelTab`/`fundTab`/`isFundTab`/`fundSlug` vivían antes en este archivo: se movieron a
// AdminPanelContext (Task 11 del plan de menú-unificado, ver
// docs/superpowers/specs/2026-08-04-menu-unificado-design.md, Fase 3) porque `activeTab` dejó de
// ser estado local de AdminView — ahora lo comparte con el Sidebar (AdminFlyout).
//
// El `<aside className="admin-sidebar">` que antes dibujaba la navegación (con su saludo
// `useAdminGreeting` y el badge de rol `ROLE_BADGE_KEY`/`roleLabelKey` en `.sidebar-header`)
// también se retiró en esa misma Task: la navegación del panel vive ahora en el `AdminFlyout` del
// Sidebar principal, que no tiene encabezado propio. Si el negocio quiere reubicar el saludo o el
// badge de rol en otra pantalla, es una decisión de producto fuera de este plan — no un efecto
// colateral silencioso de este cambio.

export interface AdminViewProps {
  /**
   * Sección de Fundamentales con la que abrir el panel (P4-2). La pasa la ruta
   * `/admin/:section` (Pulso) o `/app/admin/:section` (Reconocimiento); sin ella el panel abre
   * donde lo dejó el usuario. Acepta también los slugs legacy (`equipo`, `tablero`), que resuelven
   * por URL pero no aparecen en el menú.
   */
  initialSection?: string;
}

export function AdminView({ initialSection }: AdminViewProps = {}) {
  const { t } = useTranslation();
  // El bloque "Fundamentales" rotula con el namespace del módulo de Reconocimiento, que es donde
  // viven sus 12 secciones.
  const { t: tr } = useTranslation('recognition');
  const { activeRoles, permissions, recognitionPermissions } = useVisibleNavLinks();
  const { activeTab, setActiveTab } = useAdminPanelContext();

  const accessibleTabs = useMemo(
    () => getAccessibleAdminSections(activeRoles, permissions, recognitionPermissions),
    [activeRoles, permissions, recognitionPermissions],
  );
  const visibleTabs = useMemo(
    () => TAB_ORDER.filter(tab => accessibleTabs.includes(tab)),
    [accessibleTabs],
  );

  // Secciones de Fundamentales permitidas para estos roles y el permiso efectivo, en el orden
  // de la maqueta.
  const visibleFundSections = useMemo<RecognitionAdminSection[]>(
    () => getAccessibleRecognitionSections(activeRoles, recognitionPermissions),
    [activeRoles, recognitionPermissions],
  );

  // Un deep-link solo abre una sección que el usuario tenga permitida; si no, se ignora y el
  // panel abre por su vía normal. Los slugs legacy exigen acceso al bloque, no a una sección.
  // Las secciones de Pulso/Admin General también aceptan deep-link desde 2026-10-29: la ruta vieja
  // `/usuarios` redirige a `/admin/administradores`.
  const requestedTab = useMemo<PanelTab | null>(() => {
    if (!initialSection) return null;
    if (visibleTabs.includes(initialSection as AdminSection)) return initialSection as AdminSection;
    if (visibleFundSections.includes(initialSection as RecognitionAdminSection)) {
      return fundTab(initialSection);
    }
    if (LEGACY_SECTION_SLUGS.includes(initialSection) && visibleFundSections.length > 0) {
      return fundTab(initialSection);
    }
    return null;
  }, [initialSection, visibleTabs, visibleFundSections]);

  const [builder, setBuilder] = useState<BuilderState | null>(null);
  const [pendingTab, setPendingTab] = useState<PanelTab | null>(null);

  // `previousTabRef` es la memoria de "última pestaña reconocida" que usan los dos efectos de
  // abajo (deep-link y guard+saneo) para distinguir un cambio YA VALIDADO de uno que el guard debe
  // interceptar. Se declara antes de ambos porque los dos lo actualizan.
  const previousTabRef = useRef(activeTab);

  // El deep-link (initialSection → requestedTab) manda sobre lo guardado, y también cuando
  // cambia dentro de la misma sesión (navegar de /app/admin/campanas a /app/admin/ganadores no
  // desmonta el panel). Este cambio NO pasa por el guard del builder (efecto de abajo):
  // `previousTabRef` se actualiza aquí mismo, así que el guard lo trata como ya reconocido, no
  // como una navegación inesperada que haya que confirmar — el guard solo protege contra clics
  // directos del propio AdminFlyout, que llama a `setActiveTab` sin pasar por este efecto (fix de
  // revisión de Task 11 — B-5: antes de este fix, cualquier cambio de `activeTab` —incluido un
  // deep-link legítimo— abría el ConfirmModal, y si el usuario cancelaba la URL quedaba
  // desincronizada del contenido mostrado).
  useEffect(() => {
    if (requestedTab) {
      previousTabRef.current = requestedTab;
      setActiveTab(requestedTab);
    }
  }, [requestedTab, setActiveTab]);

  /** Toda pestaña que el usuario puede abrir hoy, en el orden del menú. */
  const allVisibleTabs = useMemo<PanelTab[]>(
    () => [...visibleTabs, ...visibleFundSections.map(fundTab)],
    [visibleTabs, visibleFundSections],
  );

  /**
   * Slug de Fundamentales que hay que RENDERIZAR, o `null`. Se recalcula desde los permisos en
   * cada render y no se apoya en el efecto de saneo: ese corre DESPUÉS del primer pintado, y una
   * pestaña restaurada de `localStorage` con otros roles alcanzaría a montar la sección —
   * disparando sus consultas— antes de que el efecto la corrigiera.
   */
  const activeFundSlug = useMemo<string | null>(() => {
    if (!isFundTab(activeTab)) return null;
    const slug = fundSlug(activeTab);
    if (visibleFundSections.includes(slug as RecognitionAdminSection)) return slug;
    if (LEGACY_SECTION_SLUGS.includes(slug) && visibleFundSections.length > 0) return slug;
    return null;
  }, [activeTab, visibleFundSections]);

  // Saneo de una pestaña que dejó de ser accesible (cambio de permisos, o una restaurada de
  // localStorage con otro rol) y guard "confirmar antes de salir del builder", EN UN SOLO efecto
  // (fix de revisión de Task 11 — B-4): separarlos competía por la autoridad sobre `activeTab` —
  // el saneo corregía una pestaña inválida y el guard, al ver un cambio que no reconocía, la
  // revertía a la pestaña inválida original; el saneo volvía a corregirla, y así indefinidamente
  // ("Maximum update depth exceeded"). El saneo manda siempre: si la pestaña activa ya no es
  // accesible, cae a la primera disponible y actualiza `previousTabRef` de una vez (el guard no
  // vuelve a intervenir sobre una corrección automática). El guard del builder solo interviene
  // cuando la pestaña SÍ es válida pero distinta de la que tenía antes — un cambio real de
  // navegación, típicamente un clic en AdminFlyout mientras el builder sigue abierto. Los slugs
  // legacy no están en el menú (`allVisibleTabs`) pero sí son navegables mientras haya acceso al
  // bloque, así que no se sanean en ese caso.
  useEffect(() => {
    const isLegacyTab = isFundTab(activeTab) && LEGACY_SECTION_SLUGS.includes(fundSlug(activeTab));
    const skipSanitize = isLegacyTab && visibleFundSections.length > 0;

    if (!skipSanitize && allVisibleTabs.length > 0 && !allVisibleTabs.includes(activeTab)) {
      const fallbackTab = allVisibleTabs[0];
      previousTabRef.current = fallbackTab;
      setActiveTab(fallbackTab);
      return;
    }

    if (builder && activeTab !== previousTabRef.current) {
      setPendingTab(activeTab);
      setActiveTab(previousTabRef.current);
      return;
    }

    previousTabRef.current = activeTab;
  }, [activeTab, allVisibleTabs, builder, visibleFundSections, setActiveTab]);

  function isTabVisible(tab: AdminSection): boolean {
    return visibleTabs.includes(tab);
  }

  function confirmNavAway() {
    if (pendingTab) {
      setActiveTab(pendingTab);
      setBuilder(null);
      setPendingTab(null);
    }
  }

  return (
    <div className="admin-view">
      <main
        className="admin-content--standalone"
        style={builder ? { padding: 0, overflow: 'hidden' } : undefined}
      >
        {builder ? (
          <SurveyBuilderView
            mode={builder.mode}
            surveyId={builder.surveyId}
            template={builder.template}
            source={builder.source}
            onClose={() => setBuilder(null)}
          />
        ) : (
          /* La pestaña activa siempre está en allVisibleTabs (ver efecto de saneo), así que el
             contenido nunca se renderiza para una sección sin permiso. Y el contenido se MONTA
             condicionalmente, no se esconde con CSS: montarlo dispararía sus consultas. */
          <>
            {activeFundSlug && (
              <>
                {/* Encabezado del bloque Fundamentales, calcado del maquetado
                    (`plataforma/index.html:1351-1355`): título + la línea que explica que cada
                    módulo es independiente. Va FUERA de la tarjeta de la sección, como allí. */}
                <header className="adm-fund-header">
                  <h2>{tr('admin.fundPanelTitle')}</h2>
                  <p>{tr('admin.fundPanelSubtitle')}</p>
                </header>
                <SectionErrorBoundary key={activeFundSlug}>
                  {renderAdminSection(activeFundSlug)}
                </SectionErrorBoundary>
              </>
            )}
            {activeTab === 'catalogoFundamentales' && isTabVisible('catalogoFundamentales') && <CatalogoSubTab />}
            {activeTab === 'catalogoOus'           && isTabVisible('catalogoOus')           && <OUsSubTab />}
            {activeTab === 'rolesPulso'            && isTabVisible('rolesPulso')            && <RolesPulsoTab />}
            {activeTab === 'surveys'       && isTabVisible('surveys')       && <SurveysTab onOpenBuilder={builderState => setBuilder(builderState)} />}
            {activeTab === 'templates'     && isTabVisible('templates')     && <TemplatesTab onOpenBuilder={builderState => setBuilder(builderState)} />}
            {activeTab === 'analytics'     && isTabVisible('analytics')     && <AnalyticsView />}
            {activeTab === 'rules'         && isTabVisible('rules')         && <RulesTab />}
            {activeTab === 'notifications' && isTabVisible('notifications') && <NotificationsTab />}
            {activeTab === 'system'        && isTabVisible('system')        && <SystemTab />}
            {activeTab === 'monitor'       && isTabVisible('monitor')       && <MonitoreoTab />}
            {activeTab === 'trace'         && isTabVisible('trace')         && <TrazabilidadTab />}
            {activeTab === 'jobs'          && isTabVisible('jobs')          && <JobsTab />}
            {activeTab === 'gestionUsuarios' && isTabVisible('gestionUsuarios') && <GestionUsuariosTab />}
            {activeTab === 'administradores' && isTabVisible('administradores') && <GestionAdministradores />}
          </>
        )}
      </main>

      {pendingTab && (
        <ConfirmModal
          title={t('admin.builderExitTitle')}
          message={t('admin.builderExitDesc')}
          confirmText={t('admin.builderExitConfirm')}
          cancelText={t('admin.builderExitCancel')}
          type="warning"
          onConfirm={confirmNavAway}
          onClose={() => setPendingTab(null)}
        />
      )}
    </div>
  );
}
